import { jwtDecode } from "jwt-decode";

// Define keys for local storage
export const AUTH_TOKEN = "auth_token";
export const USER_ID_KEY = "userId";
export const EMAIL_KEY = "email";
export const AUTH_SESSION_KEY = "auth_session";
export const ACCOUNT_ID_KEY = "account_id";

/**
 * Check if backend token is valid
 */
export const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp > currentTime;
  } catch (error) {
    console.error("Invalid token:", error);
    return false;
  }
};

/**
 * Get auth token from localStorage
 */
export const getAuthToken = () => {
  return localStorage.getItem(AUTH_TOKEN);
};

/**
 * Set auth token in localStorage
 */
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(AUTH_TOKEN, token);
  } else {
    localStorage.removeItem(AUTH_TOKEN);
  }
};

/**
 * Get user ID (Auth0 ID) from localStorage
 */
export const getUserId = () => {
  return localStorage.getItem(USER_ID_KEY);
};

/**
 * Set user ID (Auth0 ID) in localStorage
 */
export const setUserId = (userId) => {
  if (userId) {
    localStorage.setItem(USER_ID_KEY, userId);
  } else {
    localStorage.removeItem(USER_ID_KEY);
  }
};

/**
 * Get email from localStorage
 */
export const getEmail = () => {
  return localStorage.getItem(EMAIL_KEY);
};

/**
 * Set email in localStorage
 */
export const setEmail = (email) => {
  if (email) {
    localStorage.setItem(EMAIL_KEY, email);
  } else {
    localStorage.removeItem(EMAIL_KEY);
  }
};

/**
 * Get auth session from localStorage
 */
export const getAuthSession = () => {
  const session = localStorage.getItem(AUTH_SESSION_KEY);
  return session ? JSON.parse(session) : null;
};

/**
 * Set auth session in localStorage
 */
export const setAuthSession = (session) => {
  if (session) {
    localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(AUTH_SESSION_KEY);
  }
};

/**
 * Get account ID from localStorage
 */
export const getAccountId = () => {
  return localStorage.getItem(ACCOUNT_ID_KEY);
};

/**
 * Set account ID in localStorage
 */
export const setAccountId = (accountId) => {
  if (accountId) {
    localStorage.setItem(ACCOUNT_ID_KEY, accountId);
  } else {
    localStorage.removeItem(ACCOUNT_ID_KEY);
  }
};

/**
 * Set all auth data in localStorage
 * This is the main function to use after successful login
 */
export const setAuthData = ({ token, userId, email, session, accountId }) => {
  if (token) setAuthToken(token);
  if (userId) setUserId(userId);
  if (email) setEmail(email);
  if (session) setAuthSession(session);
  if (accountId) setAccountId(accountId);
};

/**
 * Clear all auth data from localStorage
 */
export const clearAuthData = () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(USER_ID_KEY);
  localStorage.removeItem(EMAIL_KEY);
  localStorage.removeItem(AUTH_SESSION_KEY);
  localStorage.removeItem(ACCOUNT_ID_KEY);
  localStorage.removeItem("otpEntered");
  localStorage.removeItem("redirectAfterLogin");
  // Clear legacy keys
  localStorage.removeItem("authId");
  localStorage.removeItem("account_id");
};

// Legacy compatibility functions
export const getUserFromLocal = () => {
  const session = getAuthSession();
  const token = getAuthToken();

  if (!token || !isTokenValid(token)) return null;

  return {
    token,
    ...session,
  };
};

export const getLoggedInUser = getUserFromLocal;

export const setLoggedInUser = ({ token, authId, accountId, session }) => {
  if (token) setAuthToken(token);
  if (authId) setUserId(authId);
  if (accountId) setAccountId(accountId);
  if (session) setAuthSession(session);
};
